import { useEffect, useState } from "react";
import { useUserContext } from "../../contexts/UserContext";
import {
    createAEMFragmentJsonPath,
    updateAEMFragmentJsonPath
} from "../../utils/APIs";
import PrimaryModal from "../UI/Modal/PrimaryModal";
import { toast } from "react-toastify";
import { getNestedData } from "../../utils/helper";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import Loader from "../UI/Loader/Loader";
import { SecondaryButton, PrimaryButton } from "../UI";

const EditJsonPathModal = ({ onClose, type, data, onSuccess, source }) => {
    const [jsonPath, setJsonPath] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const { userData } = useUserContext();
    const { content } = useContentfulContext()

    const isEdit = type === "edit";

    useEffect(() => {
        setLoading(false); // Just used to delay render; no need to prefill jsonPath
    }, []);

    const handleSave = async () => {
        setError(""); // Reset error
        setSaving(true)
        if (!jsonPath.trim()) {
            setError(content.AEM_CONTENT_DETAILS.errors.json_path_not_empty);
            setSaving(false);
            return;
        }

        const pathData = getNestedData(data, jsonPath);

        if (!(pathData && pathData.value)) {
            setError(content.AEM_CONTENT_DETAILS.errors.path_valid);
            setSaving(false)
            return;
        }else if (pathData[":type"]?.toLowerCase() !==  "json"){
            setError("Selected fragment is not of type JSON");
            setSaving(false)
            return;
        }

        try {
            const fragmentId = userData?.defaultAEMAdobeIO?._id;

            if (!fragmentId) {
                setError(content.AEM_CONTENT_DETAILS.errors.fragment_id_missing);
                return;
            }

            if (isEdit) {
                const res = await updateAEMFragmentJsonPath(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    fragmentId,
                    { json_path: jsonPath,  source : source }
                );
                if (res.status) {
                    toast.success(res.message);
                    onSuccess?.(jsonPath, data);
                    onClose();
                }
            } else {
                const res = await createAEMFragmentJsonPath(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    {
                        fragment_id: fragmentId,
                        json_path: jsonPath,
                        source : source
                    }
                );
                if (res.status) {
                    toast.success(res.message);
                    onSuccess?.(jsonPath, data);
                    onClose();
                }
            }
        } catch (err) {
            toast.error(err.message || "Failed to save JSON path");
        } finally {
            setSaving(false)
        }
    };

    return (
        <PrimaryModal
            title={isEdit ? content.AEM_CONTENT_DETAILS.json_path.edit_path_text : content.AEM_CONTENT_DETAILS.json_path.add_path_text}
            hideModal={onClose}
            widthClass="max-w-xl"
        >
            {loading ? (
                <Loader />
            ) : (
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {content.AEM_CONTENT_DETAILS.json_path.path}
                        </label>
                        <input
                            type="text"
                            className={`w-full border rounded px-3 py-2 text-sm ${error ? "border-red-500" : "border-gray-300"
                                }`}
                            value={jsonPath}
                            onChange={(e) => setJsonPath(e.target.value)}
                            placeholder="Enter valid JSON path"
                        />
                        {error && (
                            <p className="mt-1 text-sm text-red-600">{error}</p>
                        )}
                    </div>
                    <div className="flex justify-end space-x-4">
                        <SecondaryButton type="button" handleClick={() => onClose()}>
                            {content.AEM_CONTENT_DETAILS.json_path.cancel}
                        </SecondaryButton>
                        <PrimaryButton handleClick={handleSave}>
                            {saving ? (
                                <div className="flex items-center space-x-2">
                                    <span>{isEdit ? content.AEM_CONTENT_DETAILS.json_path.update_loading : content.AEM_CONTENT_DETAILS.json_path.save_loading}</span>
                                </div>
                            ) : (
                                isEdit
                                    ? content.AEM_CONTENT_DETAILS.json_path.update
                                    : content.AEM_CONTENT_DETAILS.json_path.save
                            )}
                        </PrimaryButton>
                    </div>
                </div>
            )}
        </PrimaryModal>
    );
};

export default EditJsonPathModal;
